import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';

const TOOLTIP_SWITCH_DEFAULT_CLASS =
  'absolute z-20 flex items-center pointer-events-auto top-24 right-2 sm:top-[73px] sm:left-[65%] sm:right-auto';
const TOOLTIP_SWITCH_LABEL_CLASS =
  'inline-flex shrink-0 cursor-pointer select-none items-center gap-2 whitespace-nowrap';
const TOOLTIP_SWITCH_TRACK_BASE_CLASS = 'w-9 h-5 rounded-full transition-colors';
const TOOLTIP_SWITCH_KNOB_BASE_CLASS =
  'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform';

/** Isolated tooltip switch - owns its own state so parent does not re-render on toggle */
const TooltipSwitch = memo(function TooltipSwitch({
  chartRef,
  tooltipEnabledRef,
  chartOverlayRef,
  className,
  id,
}) {
  const [tooltipEnabled, setTooltipEnabled] = useState(true);
  const tooltipEnabledStateRef = useRef(true);
  const chartRefRef = useRef(chartRef);
  const chartOverlayRefRef = useRef(chartOverlayRef);

  chartRefRef.current = chartRef;
  chartOverlayRefRef.current = chartOverlayRef;
  tooltipEnabledStateRef.current = tooltipEnabled;

  useEffect(() => {
    tooltipEnabledRef.current = tooltipEnabled;
  }, [tooltipEnabled, tooltipEnabledRef]);

  const wrapperClassName = useMemo(
    () => className || TOOLTIP_SWITCH_DEFAULT_CLASS,
    [className]
  );
  const labelTitle = useMemo(
    () => (tooltipEnabled ? 'Hide tooltip' : 'Show tooltip'),
    [tooltipEnabled]
  );
  const trackClassName = useMemo(
    () => `${TOOLTIP_SWITCH_TRACK_BASE_CLASS} ${tooltipEnabled ? 'bg-indigo-500' : 'bg-gray-300'}`,
    [tooltipEnabled]
  );
  const knobClassName = useMemo(
    () => `${TOOLTIP_SWITCH_KNOB_BASE_CLASS} ${tooltipEnabled ? 'left-[18px]' : 'left-0.5'}`,
    [tooltipEnabled]
  );

  const handleChange = useCallback(() => {
    const newVal = !tooltipEnabledStateRef.current;
    tooltipEnabledStateRef.current = newVal;
    tooltipEnabledRef.current = newVal;
    setTooltipEnabled(newVal);

    const overlay = chartOverlayRefRef.current?.current;
    if (overlay) {
      overlay.classList.remove('opacity-0', 'pointer-events-none');
    }

    setTimeout(() => {
      const chart = chartRefRef.current?.current?.chart;
      if (chart) {
        chart.update({
          plotOptions: {
            series: {
              states: {
                inactive: { enabled: newVal },
              },
            },
          },
        });
        if (!newVal) chart.tooltip?.hide?.();
      }
      if (overlay) {
        overlay.classList.add('opacity-0', 'pointer-events-none');
      }
    }, 0);
  }, [tooltipEnabledRef]);

  return (
    <div id={id} className={wrapperClassName}>
      <label className={TOOLTIP_SWITCH_LABEL_CLASS} title={labelTitle}>
        <span className="shrink-0 text-[11px] font-medium text-gray-500">Tooltip</span>
        <div className="relative inline-block h-5 w-9 shrink-0">
          <input
            type="checkbox"
            checked={tooltipEnabled}
            onChange={handleChange}
            className="sr-only"
            aria-label="Toggle tooltip"
          />
          <div className={trackClassName}>
            <div className={knobClassName} />
          </div>
        </div>
      </label>
    </div>
  );
});

export default TooltipSwitch;
